import { useAchats, useProduits } from "../lib/hooks/useProduits";
import Title from "../components/ui/Title";
import { Alert } from "../components/ui/alert";
import BlocFacturesAPayers from "../components/factures/BlocFacturesAPayers";
import { Skeleton } from "../components/ui/skeleton";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import ProduitItem from "../components/boutique/ProduitItem";
import { useCurrentUser } from "../lib/hooks/useCurrentUser";
import { useHistoriqueAbonnements } from "../lib/hooks/useAbonnements";
import { useMemo } from "react";
import { useFactures } from "../lib/hooks/useFacture";
import { FileBox, Inbox } from "lucide-react";

const BoutiquePage = () => {
  const [parent] = useAutoAnimate();
  const { data: user, isError: errorUser } = useCurrentUser({
    refetchOnWindowFocus: false,
  });
  const { data: produits, isError: errorProduits } = useProduits({
    refetchOnWindowFocus: false,
  });
  const { data: achats, isError: errorAchats } = useAchats();
  const { data: factures, isError: errorFactures } = useFactures();
  const { data: abonnements } = useHistoriqueAbonnements();

  const isProductBought = (id: string) => {
    if (!achats) return true;
    const match = achats.filter((prod) => prod.id === id);
    if (match.length > 0) return true;
    return false;
  };

  const hasFactureEnAttente = (productId: string) => {
    if (!factures) return true;
    return factures.some((f) => f.produitId === productId && !f.payee);
  };

  const peutAcheterEtiquettes = useMemo(() => {
    if (!abonnements) return false;
    const abonnementActif = abonnements.filter((a) => a.actif);
    if (abonnementActif.length === 0) return false;
    return abonnementActif.some((a) => a.citeo === true);
  }, [abonnements]);

  return (
    <div className="mx-auto w-full max-w-7xl pb-8">
      <div className="mb-7">
        <Title>La boutique</Title>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 md:text-base">
          Retrouvez les ressources pratiques réservées aux abonnés et adhérents.
        </p>
      </div>

      <BlocFacturesAPayers />

      {(errorAchats || errorFactures || errorProduits || errorUser) && (
        <Alert variant="destructive" className="mb-6">
          Impossible de charger la boutique. Réessayez dans quelques instants.
        </Alert>
      )}

      {!(errorAchats || errorFactures || errorProduits || errorUser) && (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_40px_-34px_rgba(15,23,42,0.5)]">
          <div className="flex items-center gap-3 border-b border-slate-100 bg-[#fbfaf6] px-5 py-4 sm:px-6">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f1e8c9] text-[#755c14]">
              <FileBox className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-bold text-slate-950">Ressources disponibles</h2>
            </div>
          </div>

          <div className="divide-y divide-slate-100" ref={parent}>
            {(!factures || !produits || !achats || !user) && (
              <div className="space-y-4 p-5 sm:p-6">
                <Skeleton className="h-52 w-full rounded-xl" />
                <Skeleton className="h-52 w-full rounded-xl" />
              </div>
            )}

            {!!achats && !!factures && !!produits && !!user && produits.length === 0 && (
              <div className="flex flex-col items-center px-5 py-12 text-center text-slate-500">
                <Inbox className="mb-3 h-8 w-8 text-slate-300" />
                <span className="font-medium text-slate-700">Aucune ressource disponible</span>
                <span className="mt-1 text-sm">Les nouvelles ressources apparaîtront ici.</span>
              </div>
            )}

            {!!achats && !!factures && !!produits && !!user &&
              produits.map((prod) => (
                <ProduitItem
                  produit={prod}
                  isDisabled={
                    (!user.raisonSociale && user.personneMorale) ||
                    (!user.prenom && !user.personneMorale) ||
                    !peutAcheterEtiquettes
                  }
                  key={prod.id}
                  isProductBought={isProductBought(prod.id)}
                  hasFactureEnAttente={hasFactureEnAttente(prod.id)}
                />
              ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default BoutiquePage;
